import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Key,
  Loader2,
  Plus,
  Copy,
  Trash2,
  BookOpen,
  Terminal,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

function StatusDot({ ok }) {
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`}
    />
  );
}

function StatusPill({ status }) {
  const n = Number(status);
  const good = n >= 200 && n < 300;
  const warn = n >= 300 && n < 400;
  const bad = n >= 400;
  const cls = good
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : warn
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : bad
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-ink-50 border-ink-100 text-ink-700";
  return (
    <span
      className={`inline-flex items-center font-mono text-[11px] px-2 py-0.5 rounded-sm border ${cls}`}
    >
      {status || "—"}
    </span>
  );
}

function fmtTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function SettingsApi() {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [tier, setTier] = useState("free");
  const [keys, setKeys] = useState([]);
  const [log, setLog] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null); // one-time secret display
  const [expandedLog, setExpandedLog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, k, l] = await Promise.all([
        http.get("/api-keys/eligibility"),
        http.get("/api-keys"),
        http.get("/api-keys/log?limit=200"),
      ]);
      setAllowed(!!e.data.allowed);
      setTier(e.data.tier || "free");
      setKeys(k.data.keys || []);
      setLog(l.data.entries || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load API settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      // Refresh the log every 20s so users see live hits.
      http
        .get("/api-keys/log?limit=200")
        .then(({ data }) => setLog(data.entries || []))
        .catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const { data } = await http.post("/api-keys", { label: newLabel.trim() });
      setFreshKey(data);
      setNewLabel("");
      setOpenCreate(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.label}"? CRMs or scripts using it will stop working immediately.`)) return;
    try {
      await http.delete(`/api-keys/${k.id}`);
      toast.success("Key revoked.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to revoke key");
    }
  };

  const copy = (val, msg = "Copied.") => {
    navigator.clipboard.writeText(val).then(() => toast.success(msg));
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-ink-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading API settings…
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="settings-api-page">
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/dashboard"
          className="text-ink-500 hover:text-ink-900 text-xs uppercase tracking-widestx font-mono flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
      </div>

      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
            Settings
          </div>
          <h1 className="font-display text-3xl font-extrabold text-ink-900 mt-1">
            Developer API
          </h1>
          <p className="text-sm text-ink-500 mt-2 max-w-2xl">
            Enrich leads, discover work emails, and find mobile numbers from your CRM, Zapier,
            script, or automation stack. API calls share your dashboard's monthly quota and
            credit balances — no new SKUs.
          </p>
        </div>
        <Link
          to="/developers"
          className="inline-flex items-center gap-2 text-sm font-medium text-ink-900 hover:text-brand-700"
          data-testid="api-docs-link"
        >
          <BookOpen className="w-4 h-4" /> Developer docs
        </Link>
      </div>

      {!allowed ? (
        <div
          className="rounded-sm border border-ink-100 bg-white p-10 text-center max-w-2xl mx-auto"
          data-testid="api-upgrade-cta"
        >
          <Sparkles className="w-6 h-6 text-brand-700 mx-auto" />
          <h2 className="font-display text-xl font-bold text-ink-900 mt-3">
            API access is a <span className="text-brand-700">Growth</span> perk
          </h2>
          <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
            Upgrade to Growth, Scale, or Enterprise to generate API keys and push leads into
            LeadIQ Pro from any system you run.
          </p>
          <p className="text-xs text-ink-400 mt-1">
            Current plan: <span className="font-mono uppercase">{tier}</span>
          </p>
          <Link to="/pricing">
            <Button className="mt-5 gap-2" data-testid="api-view-plans-btn">
              <Sparkles className="w-4 h-4" /> View plans
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Keys panel */}
          <section className="rounded-sm border border-ink-100 bg-white overflow-hidden mb-8">
            <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-ink-100">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-ink-500" />
                <h2 className="font-semibold text-ink-900">API keys</h2>
                <span className="text-xs text-ink-400">
                  {keys.filter((k) => k.active).length} active
                </span>
              </div>
              <Button
                onClick={() => setOpenCreate(true)}
                size="sm"
                className="gap-2"
                data-testid="api-create-key-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Create key
              </Button>
            </header>

            {keys.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-ink-500">
                No keys yet. Create one to start calling the LeadIQ Pro API from your tools.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widestx font-mono text-ink-400 border-b border-ink-100 bg-ink-50/50">
                      <th className="py-2.5 px-5">Label</th>
                      <th className="py-2.5 px-5">Prefix</th>
                      <th className="py-2.5 px-5">Created</th>
                      <th className="py-2.5 px-5">Last used</th>
                      <th className="py-2.5 px-5">Status</th>
                      <th className="py-2.5 px-5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => (
                      <tr
                        key={k.id}
                        className="border-b border-ink-100/60 hover:bg-ink-50/40"
                        data-testid={`api-key-row-${k.prefix}`}
                      >
                        <td className="py-3 px-5 font-medium text-ink-900">{k.label}</td>
                        <td className="py-3 px-5">
                          <button
                            onClick={() => copy(k.prefix, "Prefix copied.")}
                            className="inline-flex items-center gap-1 font-mono text-xs text-ink-700 hover:text-brand-700"
                          >
                            {k.prefix}…
                            <Copy className="w-3 h-3 opacity-60" />
                          </button>
                        </td>
                        <td className="py-3 px-5 text-xs text-ink-500">{fmtTs(k.created_at)}</td>
                        <td className="py-3 px-5 text-xs text-ink-500">{fmtTs(k.last_used_at)}</td>
                        <td className="py-3 px-5">
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <StatusDot ok={k.active} />
                            {k.active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className="py-3 px-5 text-right">
                          {k.active && (
                            <button
                              onClick={() => revoke(k)}
                              className="p-1.5 text-ink-400 hover:text-rose-600 rounded-sm hover:bg-rose-50"
                              title="Revoke"
                              data-testid={`api-revoke-${k.prefix}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Request log */}
          <section className="rounded-sm border border-ink-100 bg-white overflow-hidden">
            <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-ink-100">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-ink-500" />
                <h2 className="font-semibold text-ink-900">Request log</h2>
                <span className="text-xs text-ink-400">last 200 calls · live</span>
              </div>
            </header>
            {log.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-ink-500">
                No API calls yet. Hit a <span className="font-mono text-ink-700">/api/v1/</span>
                {" "}endpoint with your key to see live requests here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widestx font-mono text-ink-400 border-b border-ink-100 bg-ink-50/50">
                      <th className="py-2.5 px-5">Time</th>
                      <th className="py-2.5 px-5">Method</th>
                      <th className="py-2.5 px-5">Path</th>
                      <th className="py-2.5 px-5">Status</th>
                      <th className="py-2.5 px-5 text-right">Latency</th>
                      <th className="py-2.5 px-5">Key</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((row) => (
                      <>
                        <tr
                          key={row.id}
                          onClick={() => setExpandedLog(expandedLog === row.id ? null : row.id)}
                          className="border-b border-ink-100/60 hover:bg-ink-50/40 cursor-pointer"
                          data-testid={`api-log-row`}
                        >
                          <td className="py-2.5 px-5 text-xs text-ink-500 font-mono">
                            {fmtTs(row.ts)}
                          </td>
                          <td className="py-2.5 px-5 font-mono text-xs text-ink-700">{row.method}</td>
                          <td className="py-2.5 px-5 font-mono text-xs text-ink-900 truncate max-w-[320px]">
                            {row.path}
                          </td>
                          <td className="py-2.5 px-5">
                            <StatusPill status={row.status} />
                          </td>
                          <td className="py-2.5 px-5 text-xs text-ink-500 text-right font-mono">
                            {row.latency_ms}ms
                          </td>
                          <td className="py-2.5 px-5 text-xs text-ink-500 font-mono truncate max-w-[160px]">
                            {row.key_prefix}
                          </td>
                        </tr>
                        {expandedLog === row.id && (
                          <tr className="bg-ink-900 text-ink-50">
                            <td colSpan={6} className="px-5 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                                    Request body
                                  </div>
                                  <pre className="font-mono text-[11px] whitespace-pre-wrap break-all">
                                    {row.req_preview || "(empty)"}
                                  </pre>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                                    Response (preview)
                                  </div>
                                  <pre className="font-mono text-[11px] whitespace-pre-wrap break-all">
                                    {row.resp_preview || "(empty)"}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* Create-key dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create a new API key</DialogTitle>
            <DialogDescription>
              Give your key a label so you can tell your HubSpot workflow from your personal
              scripts. The secret is shown exactly once — copy it to a safe place.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widestx text-ink-500">
                Label
              </Label>
              <Input
                autoFocus
                required
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="HubSpot workflow"
                maxLength={80}
                className="mt-1"
                data-testid="api-new-key-label"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="gap-2" data-testid="api-new-key-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create key
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fresh key one-time secret dialog */}
      <Dialog open={!!freshKey} onOpenChange={(v) => !v && setFreshKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Save this secret now
            </DialogTitle>
            <DialogDescription>
              This is the <b>only time</b> we'll show the full secret. Copy it into your CRM,
              Zapier action, or secrets manager before closing this dialog.
            </DialogDescription>
          </DialogHeader>
          {freshKey && (
            <div className="space-y-3 text-sm">
              <div className="rounded-sm border border-ink-100 bg-ink-50 p-3">
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mb-1">
                  Label
                </div>
                <div className="text-ink-900">{freshKey.label}</div>
              </div>
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase tracking-widestx font-mono text-emerald-800 mb-1 flex items-center justify-between">
                  Secret
                  <button
                    onClick={() => copy(freshKey.secret, "Secret copied.")}
                    className="inline-flex items-center gap-1 text-emerald-900 hover:text-emerald-700"
                    data-testid="api-copy-secret"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <div className="font-mono text-sm break-all text-ink-900">{freshKey.secret}</div>
              </div>
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Paste it as{" "}
                <code className="font-mono bg-white px-1 py-0.5 rounded">
                  Authorization: Bearer {freshKey.prefix}…
                </code>{" "}
                in your HTTP client. If you lose it, revoke the key and create a new one.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setFreshKey(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
